import React, { useState } from "react";
import { Button, Select, Card, Typography, message, Space, Spin } from "antd";
import { PlusOutlined, UserAddOutlined } from "@ant-design/icons";
import axios from "axios";
import constants from "../constants";
import { useAuth } from "../contexts/authContext";

const { Title, Text } = Typography;
const { Option } = Select;

function AddPartner() {
  const [loading, setLoading] = useState(false);
  const [fetchingPartners, setFetchingPartners] = useState(false);
  const [showSelect, setShowSelect] = useState(false);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const { getToken } = useAuth();
  // Fetch partners from Fillout API
  const fetchPartnersFromFillout = async () => {
    try {
      setFetchingPartners(true);

      // Replace with your actual Fillout API endpoint
      const filloutApiUrl = process.env.REACT_APP_FILLOUT_API_URL || "https://api.fillout.com/v1/api/forms/qcGK2brcWNus/submissions";
      const filloutApiKey = process.env.REACT_APP_FILLOUT_API_KEY;

      const response = await axios.get(filloutApiUrl, {
        headers: {
          Authorization: `Bearer ${filloutApiKey}`,
          "Content-Type": "application/json",
        },
      });

      // Process the response to extract partner data
      // Adjust this based on your Fillout form structure
      const partnerData =
        response.data.responses?.map((submission, index) => ({
          id: submission.submissionId || index,
          name: submission.questions?.find((q) => q.name === "Full Name")?.value,
          email: submission.questions?.find((q) => q.name === "Email")?.value || "",
          phone: submission.questions?.find((q) => q.name === "Phone number")?.value || "",
          joiningDate: new Date(submission.submissionTime),
          rawData: submission, // Keep original data for reference
        })) || [];

      setPartners(partnerData);
      setShowSelect(true);

      if (partnerData.length === 0) {
        message.warning("No partner applications found in Fillout form");
      } else {
        message.success(`Found ${partnerData.length} partner applications`);
      }
    } catch (error) {
      console.error("Error fetching partners from Fillout:", error);
      message.error("Failed to fetch partner applications. Please check your Fillout API configuration.");
    } finally {
      setFetchingPartners(false);
    }
  };

  // Save partner to database
  const savePartnerToDatabase = async (partner) => {
    try {
      setLoading(true);

      const partnerData = {
        name: partner.name,
        email: partner.email,
        phone: partner.phone,
        joiningDate: new Date(),
      };

      const token = await getToken();
      const headers = {
        authorization: `bearer ${token}`,
      };

      const response = await axios.post(`${constants.NEW_DOMAIN}/api/v1/channelPartner/addPartner`, partnerData, {
        headers,
      });

      message.success(`Partner "${partner.name}" has been successfully added to the database!`);
      console.log("Partner saved successfully:", response.data);

      // Reset the form
      setSelectedPartner(null);
      setShowSelect(false);
      setPartners([]);
    } catch (error) {
      console.error("Error saving partner:", error);

      if (error.response?.status === 409) {
        message.error("This partner already exists in the database");
      } else if (error.response?.status === 400) {
        message.error("Invalid partner data. Please check the information.");
      } else {
        message.error(error.response?.data?.message || "Failed to save partner. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerSelect = (value) => {
    const partner = partners.find((p) => p.id === value);
    setSelectedPartner(partner);
  };

  const handleSavePartner = () => {
    if (selectedPartner) {
      savePartnerToDatabase(selectedPartner);
    }
  };

  const handleCancel = () => {
    setShowSelect(false);
    setSelectedPartner(null);
    setPartners([]);
  };

  return (
    <div style={{ padding: 24, background: "#f7f8fa", minHeight: "100%" }}>
      <Card
        bordered
        style={{
          borderRadius: 12,
          maxWidth: 600,
          margin: "0 auto",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <UserAddOutlined style={{ fontSize: 24, color: "#1890ff", marginBottom: 16 }} />
          <Title level={3} style={{ margin: 0, color: "#262626" }}>
            Add New Partner
          </Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            Import partner applications from Fillout form and add them to the system
          </Text>
        </div>

        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {!showSelect ? (
            <div style={{ textAlign: "center" }}>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={fetchPartnersFromFillout}
                loading={fetchingPartners}
                style={{
                  height: 48,
                  paddingLeft: 32,
                  paddingRight: 32,
                  fontSize: 16,
                  borderRadius: 8,
                }}
              >
                {fetchingPartners ? "Fetching Partners..." : "Add Partner"}
              </Button>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 16, marginBottom: 8, display: "block" }}>
                  Select a Partner to Add:
                </Text>
                <Select
                  placeholder="Choose a partner application"
                  style={{ width: "100%" }}
                  size="large"
                  onChange={handlePartnerSelect}
                  value={selectedPartner?.id}
                  showSearch
                  filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                >
                  {partners.map((partner) => (
                    <Option key={partner.id} value={partner.id}>
                      <div style={{ fontWeight: 500 }}>{partner.name}</div>
                    </Option>
                  ))}
                </Select>
              </div>

              {selectedPartner && (
                <Card
                  size="small"
                  style={{
                    backgroundColor: "#f7f8fa",
                    border: "1px solid #971eff",
                    marginBottom: 16,
                  }}
                >
                  <Title level={5} style={{ margin: "0 0 8px 0", color: "#971eff" }}>
                    Selected Partner Details:
                  </Title>
                  <div style={{ fontSize: 14 }}>
                    <div>
                      <strong>Name:</strong> {selectedPartner.name}
                    </div>
                    <div>
                      <strong>Email:</strong> {selectedPartner.email}
                    </div>
                    <div>
                      <strong>Password:</strong> {selectedPartner.name?.split(" ")[0]?.toLowerCase() + "@1234"}
                    </div>
                    <div>
                      <strong>Phone:</strong> {selectedPartner.phone}
                    </div>
                    <div>
                      <strong>Joining Date:</strong> {selectedPartner?.joiningDate?.toLocaleDateString() || "----------"}
                    </div>
                  </div>
                </Card>
              )}

              <Space style={{ width: "100%", justifyContent: "center" }}>
                <Button type="primary" size="large" onClick={handleSavePartner} loading={loading} disabled={!selectedPartner} style={{ borderRadius: 8 }}>
                  {loading ? "Saving..." : "Onboard Partner"}
                </Button>
                <Button size="large" onClick={handleCancel} style={{ borderRadius: 8 }}>
                  Cancel
                </Button>
              </Space>
            </div>
          )}
        </Space>
      </Card>
    </div>
  );
}

export default AddPartner;
